import React from 'react';
import { CustomFieldsType } from '../../../../lib/api';
import {
  EMAIL_REGEXP,
  SHORT_PLAIN_TEXT_LENGTH,
  REQUIRED,
  FIRST_SPACE_TRIMMER,
  REQUIRED_CHECKBOX,
  STRING_WITHOUT_HTML_REGEX,
  REG_DATA, MEDIUM_PLAIN_TEXT_LENGTH,
  MEDIUM_PLAIN_TEXT_MAX_LENGTH,
  NO_XSS_CONTENT
} from '../../../../lib/constants';
import { getItemFromSessionStorage } from '../../../../lib/helpers/storage';
import { CustomFieldType } from './RegistrationForm';

export type Formatter = (value: string) => string;

export type ValidatorResultType = {
  valid: boolean;
  validationMessage: string | null;
};

export type ValidatorType = {
  validator: (value: string) => boolean;
  validationMessage: string;
};

type InputValidatorType = (isValidFn: ValidatorType, value: string) => ValidatorResultType;

export interface FieldsProps {
  externalFieldId: string;
  name: string;
  label: string;
  required: boolean;
  validators?: ValidatorType[];
  optional?: boolean;
  formatters?: Formatter[];
  order: number;
  group?: FieldsProps[];
  fieldType?: string;
  optionsList?: string[];
  triaged?: boolean;
  visible?:boolean;
}

export const handleValidation: InputValidatorType = (isValidFn: ValidatorType, val: string): ValidatorResultType => {
  const valid = isValidFn.validator(val);
  const validationMessage = valid ? null : isValidFn.validationMessage;

  return { valid: valid, validationMessage: validationMessage };
};

const REQUIRED_MESSAGE = 'This field is required.';
const REQUIRED_MESSAGE_FOR_LABEL = ' is required.';

const EMAIL_ERROR_MESSAGE = 'Enter a valid email address.';
const MAX_LENGTH_ERROR_MESSAGE = 'This field allows 255 characters max.';
const MAX_MEDIUM_PLAIN_LENGTH_ERROR_MESSAGE = `This field allows ${MEDIUM_PLAIN_TEXT_MAX_LENGTH} characters max.`;
const XSS_ERROR_MESSAGE = 'Invalid characters used.';

const emailValidators = [
  { validator: EMAIL_REGEXP, validationMessage: EMAIL_ERROR_MESSAGE },
  { validator: SHORT_PLAIN_TEXT_LENGTH, validationMessage: MAX_LENGTH_ERROR_MESSAGE }
];

const requiredEmailValidators = [
  { validator: REQUIRED, validationMessage: 'Email'+ REQUIRED_MESSAGE_FOR_LABEL },
  ...emailValidators
];
const textValidators = [{ validator: SHORT_PLAIN_TEXT_LENGTH, validationMessage: MAX_LENGTH_ERROR_MESSAGE }];
const nameValidators = [
  { validator: NO_XSS_CONTENT, validationMessage: XSS_ERROR_MESSAGE },
  { validator: SHORT_PLAIN_TEXT_LENGTH, validationMessage: MAX_LENGTH_ERROR_MESSAGE }
];
const textareaValidators = [{ validator: MEDIUM_PLAIN_TEXT_LENGTH, validationMessage: MAX_MEDIUM_PLAIN_LENGTH_ERROR_MESSAGE }];
const requiredTextValidators = (label = '') => {
  const formattedLabel = label.replace(STRING_WITHOUT_HTML_REGEX, '');
  return [
    { validator: REQUIRED, validationMessage: label ? (formattedLabel + REQUIRED_MESSAGE_FOR_LABEL) : REQUIRED_MESSAGE },
    { validator: SHORT_PLAIN_TEXT_LENGTH , validationMessage: MAX_LENGTH_ERROR_MESSAGE }
  ];
};

const requiredNameValidators = (label = '') => {
  const formattedLabel = label.replace(STRING_WITHOUT_HTML_REGEX, '');
  return [
    { validator: REQUIRED, validationMessage: label ? (formattedLabel + REQUIRED_MESSAGE_FOR_LABEL) : REQUIRED_MESSAGE },
    { validator: NO_XSS_CONTENT, validationMessage: XSS_ERROR_MESSAGE },
    { validator: SHORT_PLAIN_TEXT_LENGTH, validationMessage: MAX_LENGTH_ERROR_MESSAGE }
  ];
};

const requiredTextareaValidators = (label = '') => {
  const formattedLabel = label.replace(STRING_WITHOUT_HTML_REGEX, '');
  return [
    { validator: REQUIRED, validationMessage: label ? (formattedLabel + REQUIRED_MESSAGE_FOR_LABEL) : REQUIRED_MESSAGE },
    { validator: MEDIUM_PLAIN_TEXT_LENGTH, validationMessage: MAX_MEDIUM_PLAIN_LENGTH_ERROR_MESSAGE }
  ];
};
const requiredCheckBoxValidator = [{ validator: REQUIRED_CHECKBOX, validationMessage: REQUIRED_MESSAGE }];

const trimFirstSpace = [FIRST_SPACE_TRIMMER];

const mandatoryEmailValidators = (mandatory: boolean) => mandatory ? requiredEmailValidators : emailValidators;
const mandatoryCustomFieldValidators = (mandatory: boolean, label?: string) => mandatory ? requiredTextValidators(label) : textValidators;
const mandatoryNameValidators = (mandatory: boolean, label?: string) => mandatory ? requiredNameValidators(label) : nameValidators;
const mandatoryCheckBoxValidator = (mandatory: boolean): ValidatorType[] => mandatory ? requiredCheckBoxValidator : [];
const mandatoryTextareaValidator = (mandatory: boolean, label?: string): ValidatorType[] => mandatory ? requiredTextareaValidators(label) : textareaValidators;

const customFieldValidators = (formKey: string | undefined, fieldType: string | undefined, mandatory: boolean, label?: string, visible?: boolean): ValidatorType[] => {
  const isRequired = mandatory && visible !== false;
  if (formKey === 'email' || formKey === 'assistantEmail') {
    return mandatoryEmailValidators(isRequired);
  } else if (formKey === 'firstName' || formKey === 'lastName') {
    return mandatoryNameValidators(isRequired, label);
  } else if (fieldType === CustomFieldType.checkbox) {
    return mandatoryCheckBoxValidator(isRequired);
  } else if (fieldType === CustomFieldType.textarea) {
    return mandatoryTextareaValidator(isRequired, label);
  }
  else {
    return mandatoryCustomFieldValidators(isRequired, label);
  }

};

export type ValuesProps = {
  [key: string]: string
};

export type TouchedProps = {
  [key: string]: boolean
};

export type ErrorProps = {
    [key: string]: ValidatorResultType[]
};
interface ReducerProps {
  isDisabledSubmit: boolean;
  values: ValuesProps;
  touched: TouchedProps;
  errors: ErrorProps;
}

type PayloadProp = {
  key: string;
  value: string;
  validators?: ValidatorType[];
};

export enum Types {
  onChangeAction = 'ON_CHANGE',
  onBlurAction = 'ON_BLUR',
}

export const AttendanceMethodOptionLabels = {
  'IN_PERSON': 'In Person',
  'VIRTUAL': 'Virtual',
  'BOTH': 'Both',
};

interface Payload {
  type: Types;
  payload: PayloadProp;
}

type UseValuesProps = {
  state: ReducerProps;
  handleChange: (
    key: string,
    value: string,
    validators: ValidatorType[]) => void;
  handleBlur: (
    key: string,
    value: string,
    validators: ValidatorType[]) => void;
}

const createFieldsSetting = (customFields:CustomFieldsType[]): FieldsProps[] => {
  if (!customFields?.length) return [];
  return customFields
    .sort((a, b) => a.positionOrder - b.positionOrder)
    .map(({
      default: isDefault,
      formKey,
      mandatory,
      positionOrder,
      customFieldId,
      label,
      fieldType,
      optionsList,
      triaged,
      visible,
      externalFieldId,
    }) => {
      return ({
        name: isDefault ? formKey as string : `customField_${customFieldId}`,
        label: label,
        required: mandatory,
        optional: !mandatory,
        validators: customFieldValidators(formKey, fieldType, mandatory, label, visible),
        formatters: trimFirstSpace,
        order: positionOrder,
        fieldType: fieldType ?? '',
        optionsList: optionsList ?? [],
        triaged: triaged,
        visible: visible,
        externalFieldId: externalFieldId ?? ''
      });
    });
};

const settings = (customFields: CustomFieldsType[] = []): FieldsProps[] =>
  createFieldsSetting(customFields)
;
const areVisibleMandatoryFieldsFilled = (fields: FieldsProps[], values: ValuesProps) => {
  return fields.every(field => {
    if (field.required && field.visible !== false) {
      const value = values[field.name];
      if (field.fieldType === CustomFieldType.checkbox) {
        return value === 'TRUE';
      }
      return value?.trim().length > 0;
    }
    return true;
  });
};

const isFormValid = (errors: Record<string, ValidatorResultType[]>) => Object.values(errors)
  .every((errorArr) => errorArr.every(err => err.valid));

const createReducer = (fields: FieldsProps[]) => (state: ReducerProps, action: Payload): ReducerProps => {
  const key = action.payload.key;
  const value = action.payload.value;
  const validators = action.payload.validators;
  const validationResults = (validators ?? []).map(validator => handleValidation(validator, value));
  const updatedErrors = { ...state.errors, [key]: validationResults };
  const updatedValues = { ...state.values, [key]: value };
  const isDisabledSubmit = !isFormValid(updatedErrors) ||
      !areVisibleMandatoryFieldsFilled(fields, updatedValues);

  switch (action.type) {
  case Types.onChangeAction:
    return {
      ...state,
      values: updatedValues,
      errors: updatedErrors,
      isDisabledSubmit
    };
  case Types.onBlurAction:
    return {
      ...state,
      errors: updatedErrors,
      touched: { ...state.touched, [key]: true },
      isDisabledSubmit
    };
  default:
    return state;
  }
};

const useValues = (fields: FieldsProps[], selectedTriageField?: Record<string, string>, urlParams?: Record<string, string | null>): UseValuesProps => {
  const storedRegistrationData = JSON.parse(getItemFromSessionStorage(REG_DATA) as string || '{}');

  const initialValues = fields.reduce((prev, next) => {
    const isStoredField = Reflect.has(storedRegistrationData, next.name);
    const isFieldTriageQuestion = !isStoredField && !!selectedTriageField?.[next.name];
    const hasUrlParam = !isStoredField && !isFieldTriageQuestion && !!urlParams?.[next.name];
    const initialValue = isStoredField
      ? storedRegistrationData[next.name]
      : isFieldTriageQuestion
        ? selectedTriageField?.[next.name]
        : hasUrlParam
          ? urlParams?.[next.name] || ''
          : next.fieldType === CustomFieldType.checkbox ? 'FALSE' : '';
    const isTouched = isStoredField || hasUrlParam || isFieldTriageQuestion;
    const validation = isTouched
      ? (next.validators ?? []).map(isValidFn => handleValidation(isValidFn, initialValue))
      : (next.validators ?? []).map(() => ({ valid: true, validationMessage: null }));

    const updatedErrors = { ...prev.errors, [next.name]: validation };
    const updatedValues = { ...prev.values, [next.name]: initialValue };
    const updatedTouched = { ...prev.touched, [next.name]: isTouched };

    return {
      errors: updatedErrors,
      values: updatedValues,
      touched: updatedTouched,
      isDisabledSubmit: !isFormValid(updatedErrors) || !areVisibleMandatoryFieldsFilled(fields, updatedValues),
    };
  }, {
    isDisabledSubmit: true,
    values: {},
    touched: {},
    errors: {}
  });

  const [state, dispatch] = React.useReducer(createReducer(fields), initialValues);

  const handleChange = (key: string, value: string, validators: ValidatorType[]) => {
    dispatch({
      type: Types.onChangeAction,
      payload: { key, value, validators }
    });
  };

  const handleBlur = (key: string, value: string, validators: ValidatorType[]) => {
    dispatch({
      type: Types.onBlurAction,
      payload: { key, value, validators }
    });
  };

  return {
    state,
    handleChange,
    handleBlur,
  };
};

export { settings, useValues, mandatoryCheckBoxValidator };